import {
  BookOpenIcon,
  BoxesIcon,
  CalendarClockIcon,
  CalendarDaysIcon,
  GiftIcon,
  InboxIcon,
  LayersIcon,
  LayoutDashboardIcon,
  LightbulbIcon,
  MailIcon,
  MegaphoneIcon,
  NetworkIcon,
  PackageIcon,
  ReceiptIcon,
  RepeatIcon,
  ScrollTextIcon,
  SettingsIcon,
  ShieldCheckIcon,
  ShoppingBagIcon,
  SparklesIcon,
  SunIcon,
  TrendingUpIcon,
  TypeIcon,
  UsersIcon,
} from 'lucide-react'
import type { IconComponent } from '../ui/icon'
import type { AdminArea } from './AdminContext'
import { ADMIN_BASE } from '../utils/hosts'
import { adminSections, type AdminSectionKey } from '../data/adminSections'
import { can, canAll, type Access } from '../core/rules/permissions'
import type { Permission } from '../core/types/team'

/**
 * Het menu van het beheer: de enige bron. De zijbalk, het menu op de telefoon, de tabbalk
 * onderin (`mobile`), de "Komt binnenkort"-pagina's (`comingSoon`) en de suggestie op de
 * niet-gevonden-pagina komen allemaal hieruit. Namen en paden staan in data/adminSections.ts
 * (die kent ook de webshop). Indeling: docs/BEHEER-PLAN.md §2.
 */

/**
 * Groepen in menuvolgorde, naar hoe vaak Sarah er komt (BEHEER-PLAN §11):
 * dagelijks en wekelijks bovenaan en open; wat ze zelden aanpast (het aanbod, de site en
 * marketing) ingeklapt. 'Beheer' staat onderaan, zonder kop (Instellingen; Beveiliging zit bij je naam).
 */
export const adminNavGroups = ['Vandaag', 'Klanten', 'Winkel', 'Cijfers', 'Aanbod', 'Website & marketing', 'Beheer'] as const
export type AdminNavGroup = (typeof adminNavGroups)[number]

/** Ingeklapt tot ze hem opent (onthouden), of tot er iets in actief of gewijzigd is. */
export const collapsedNavGroups: ReadonlySet<AdminNavGroup> = new Set<AdminNavGroup>(['Aanbod', 'Website & marketing'])

export type AdminNavItem = {
  key: AdminSectionKey
  /** Routepad binnen /be ('' = het overzicht). */
  path: string
  /** Volledig adres, voor links. */
  to: string
  label: string
  icon: IconComponent
  group: AdminNavGroup
  /** Welke delen van het concept hier worden aangepast — voor het stipje "gewijzigd". */
  areas?: AdminArea[]
  /** In de tabbalk onderin op de telefoon. */
  mobile?: boolean
  /**
   * Welke bevoegdheid dit onderdeel vraagt (core/rules/permissions.ts). Ontbreekt = elke sessie.
   * Menu én route volgen dit (RequireNav); de server dwingt het af (403).
   */
  permission?: Permission
  /** Eigen bereik is genoeg (een collega ziet haar eigen agenda en kassa). Anders: voor alles. */
  own?: boolean
  /** Nog niet gebouwd: toont een "Komt binnenkort"-pagina, geen 404. */
  comingSoon?: boolean
  /** Wat het onderdeel straks doet (op de "Komt binnenkort"-pagina). */
  description?: string
  /**
   * Niet in het menu, wel bereikbaar op zijn adres. Voor een onderdeel dat nog niet gebouwd is
   * terwijl het werk al ergens anders kan (dan geen dode knop in het menu).
   */
  hidden?: boolean
}

type Options = Omit<AdminNavItem, 'key' | 'path' | 'to' | 'label' | 'icon' | 'group'>

const item = (key: AdminSectionKey, icon: IconComponent, group: AdminNavGroup, options: Options): AdminNavItem => {
  const path = adminSections[key].path
  return { key, path: path.replace(/^\//, ''), to: `${ADMIN_BASE}${path}`, label: adminSections[key].label, icon, group, ...options }
}

export const adminNav: AdminNavItem[] = [
  // Vandaag gaat over de dag in de salon. Wie geen agenda heeft (Marketing), begint bij het eerste
  // onderdeel dat ze wel mag (Dashboard).
  item('overzicht', LayoutDashboardIcon, 'Vandaag', { permission: 'agenda.view', own: true, mobile: true }),
  item('agenda', CalendarClockIcon, 'Vandaag', { permission: 'agenda.view', own: true, mobile: true }),
  // Geen eigen menu-item: voor de collega ís Vandaag haar dag (Dashboard → MyDay); Sarah opent hem
  // met de knop "Mijn dag" op het Overzicht en in de Agenda. Het adres blijft werken.
  item('mijnDag', SunIcon, 'Vandaag', { permission: 'agenda.view', hidden: true }),
  item('kassa', ReceiptIcon, 'Vandaag', { permission: 'kassa.sell', own: true, mobile: true }),
  // Dagelijks werk met een getal erop (useNavCounts): hoort in de onderbalk, niet achter Menu.
  // Vijf plus Menu past zonder afbreken op 320 px (korte namen, 11 px).
  // Inbox en de klantenlijst: alleen de eigenaar. Een collega opent een klantkaart vanuit haar eigen
  // afspraak (/be/klanten/:id, beperkt; docs/KASSA-ABONNEMENTEN.md §4).
  item('inbox', InboxIcon, 'Vandaag', { permission: 'orders.view', mobile: true }),
  item('klanten', UsersIcon, 'Klanten', { permission: 'customers.view', mobile: true }),
  // Het tegoed per klant staat in het klantdossier (Overzicht › Tegoed).
  item('cadeaubonnen', GiftIcon, 'Klanten', { permission: 'vouchers.code' }),
  // Nog niet gebouwd, en uit het menu: abonnementen beheer je nu per klant (Klanten › filter
  // Abonnement › dossier › Abonnement). Het adres toont nog "Komt binnenkort".
  item('abonnementen', RepeatIcon, 'Klanten', {
    permission: 'subscriptions.manage',
    comingSoon: true,
    hidden: true,
    description:
      'Alle abonnementen op één plek: behandelabonnementen met tegoed en maandelijkse incasso, en de productabonnementen. Met leveringen en incasso’s van deze week, mislukte betalingen en wie gepauzeerd of opgezegd heeft.',
  }),
  // Winkel: de webshop van bestelling tot schap.
  item('bestellingen', ShoppingBagIcon, 'Winkel', { permission: 'orders.view' }),
  item('producten', PackageIcon, 'Winkel', { permission: 'catalog.edit', areas: ['products'] }),
  // Voorraad per plek, met het log. Slaat per actie op, los van het concept (geen `areas`).
  item('voorraad', BoxesIcon, 'Winkel', { permission: 'stock.use' }),
  item('omzet', TrendingUpIcon, 'Cijfers', { permission: 'revenue.view' }),
  // Wat werkt (docs/INZICHT.md).
  item('inzicht', LightbulbIcon, 'Cijfers', { permission: 'insights.view' }),
  // Aanbod: wat de salon biedt en hoe het samenhangt. Zelden aangepast, dus ingeklapt.
  item('behandelingen', SparklesIcon, 'Aanbod', { permission: 'catalog.edit', areas: ['treatments'] }),
  item('protocollen', LayersIcon, 'Aanbod', { permission: 'catalog.edit', areas: ['routines'] }),
  item('huidvragen', NetworkIcon, 'Aanbod', { permission: 'catalog.edit', areas: ['concerns'] }),
  item('teksten', TypeIcon, 'Website & marketing', { permission: 'content.edit', areas: ['pages'] }),
  item('journal', BookOpenIcon, 'Website & marketing', { permission: 'content.edit', areas: ['articles'] }),
  item('social', CalendarDaysIcon, 'Website & marketing', { permission: 'marketing.social', areas: ['social'] }),
  item('ontwerpen', MegaphoneIcon, 'Website & marketing', { permission: 'marketing.social', areas: ['designs', 'brandKit'] }),
  item('nieuwsbrief', MailIcon, 'Website & marketing', { permission: 'marketing.newsletter', areas: ['newsletters'] }),
  item('instellingen', SettingsIcon, 'Beheer', { permission: 'settings.edit', areas: ['site'] }),
  // In het accountmenu (AdminAccount), bij je naam: het gaat over jouw login, niet over de zaak.
  item('beveiliging', ShieldCheckIcon, 'Beheer', { hidden: true }),
  // Wie wat deed (§10.7). Alleen-lezen.
  item('logboek', ScrollTextIcon, 'Beheer', { permission: 'audit.view' }),
]

/**
 * Mag dit account dit onderdeel zien (en openen)? Menu, zoeken en routes gebruiken dezelfde regel;
 * de server antwoordt bij de rest met 403.
 */
export const canSee = (n: AdminNavItem, access: Access) => !n.permission || (n.own ? can(access, n.permission) : canAll(access, n.permission))

export const navItem = (key: AdminSectionKey): AdminNavItem | undefined => adminNav.find((n) => n.key === key)

/**
 * Bij welk onderdeel een adres hoort (ook detailpagina's: /be/klanten/123 → Klanten).
 * Het langste passende pad wint; het overzicht alleen op /be zelf.
 */
export function sectionFor(pathname: string, items: AdminNavItem[] = adminNav): AdminNavItem | undefined {
  const path = pathname.replace(/\/+$/, '') || '/'
  return items
    .filter((n) => (n.path === '' ? path === ADMIN_BASE : path === n.to || path.startsWith(`${n.to}/`)))
    .sort((a, b) => b.to.length - a.to.length)[0]
}
